import * as React from "react";
import { ComboBox, Item, Flex, View, Text } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import type { Key } from "@react-types/shared";
import { IFilterControl, IFilterItem } from "../../FilterSetupPlaceholder";
import { useDependency } from "../../../../../../../contexts/DependencyProvider";
import { ListUserRequest } from "../../../../../../../services/soap/access/requests/ListUserRequest";
import { GetUserRequest } from "../../../../../../../services/soap/access/requests/GetUserRequest";
import { useFilterGuard } from "../../../../../../../hooks/useFilterGuard";
import styles from "./ResponsibleUserFilterControl.module.css";
import { OperatorSelector, IOperator } from "../OperatorSelector";
import { useTranslation } from "react-i18next";

/**
 * Props for the ResponsibleUserFilterControl component
 */
export interface IResponsibleUserFilterControlProps {
    /** Filter control configuration object */
    filter: IFilterControl;
    /** Callback function called when filter values change */
    onFilterChange: (filter: IFilterControl) => void;
    /** Optional disabled state */
    disabled?: boolean;
}

interface User {
    id: number;
    name: string;
}

export const ResponsibleUserFilterControl = React.forwardRef<
    HTMLDivElement,
    IResponsibleUserFilterControlProps
>((props, forwardedRef) => {
    const { filter, onFilterChange, disabled = false } = props;
    const { t } = useTranslation(["common"]);
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const { accessService, store } = useDependency();
    const userOperators = React.useMemo<IOperator[]>(
        () => [
            {
                key: "current_user",
                label: t("current_user", {
                    ns: "layout_components",
                    defaultValue: "Current User",
                }),
            },
            {
                key: "selected_user",
                label: t("selected_user_label", {
                    ns: "layout_components",
                    defaultValue: "Selected User",
                }),
            },
        ],
        [t]
    );
    const apiDebounceRef = React.useRef<NodeJS.Timeout | null>(null);
    const isInternalUpdateRef = React.useRef<boolean>(false);
    const lastProcessedFilterRef = React.useRef<string>("");
    const lastCommittedFilterRef = React.useRef<{
        value: string;
        valueLabel: string;
        operator: string;
        isCurrentUser: boolean;
    } | null>(null);

    const currentFilterItem = React.useMemo(
        () =>
            filter.filterInfo[0] || {
                name: "value",
                type: "any",
                value: "",
            },
        [filter.filterInfo]
    );

    React.useEffect(() => {
        syncGuard(filter);
    }, [filter, syncGuard]);

    const getInitialOperator = () => {
        if (currentFilterItem.isCurrentUser) {
            return "current_user";
        }
        if (
            currentFilterItem.type === "current_user" ||
            currentFilterItem.type === "selected_user"
        ) {
            return currentFilterItem.type;
        }
        return "selected_user";
    };

    const [selectedKey, setSelectedKey] = React.useState<Key | null>(
        currentFilterItem.value ? String(currentFilterItem.value) : null
    );
    const [inputValue, setInputValue] = React.useState<string>(
        currentFilterItem.valueLabel || ""
    );
    const [operator, setOperator] = React.useState<string>(
        getInitialOperator()
    );
    const [currentUserInfo, setCurrentUserInfo] = React.useState<{
        id: number;
        name: string;
    } | null>(null);

    const isCurrentUserActive = operator === "current_user";
    const isDisabled = disabled || isCurrentUserActive;

    const hasFilterValueChanged = React.useCallback(
        (newValue: string, newValueLabel: string, newOperator: string, newIsCurrentUser: boolean): boolean => {
            const lastCommitted = lastCommittedFilterRef.current;
            if (!lastCommitted) return true;
            return (
                lastCommitted.value !== newValue ||
                lastCommitted.valueLabel !== newValueLabel ||
                lastCommitted.operator !== newOperator ||
                lastCommitted.isCurrentUser !== newIsCurrentUser
            );
        },
        []
    );

    React.useEffect(() => {
        if (currentFilterItem && !lastCommittedFilterRef.current) {
            const initialOperator = currentFilterItem.isCurrentUser === true
                ? "current_user"
                : "selected_user";
            lastCommittedFilterRef.current = {
                value: currentFilterItem.value || "",
                valueLabel: currentFilterItem.valueLabel || "",
                operator: initialOperator,
                isCurrentUser: currentFilterItem.isCurrentUser || false,
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await accessService.getUser(
                    new GetUserRequest(
                        store.Server,
                        store.SessionId,
                        store.UserId.toString()
                    )
                );
                if (response?.USER) {
                    const userName =
                        response.USER.contactName ||
                        response.USER.name1 ||
                        response.USER.userName ||
                        String(response.USER.id);
                    setCurrentUserInfo({
                        id: response.USER.id,
                        name: userName,
                    });
                }
            } catch (error) {
                console.error("Error fetching current user:", error);
            }
        };
        fetchCurrentUser();
    }, [accessService, store.Server, store.SessionId, store.UserId]);

    // When "Current User" is active, show current user in ComboBox without mutating parent
    React.useEffect(() => {
        if (!isCurrentUserActive || !currentUserInfo) return;
        const nextKey = String(currentUserInfo.id);
        const nextLabel = currentUserInfo.name;
        setSelectedKey((prev) => (prev === nextKey ? prev : nextKey));
        setInputValue((prev) => (prev === nextLabel ? prev : nextLabel));
    }, [currentUserInfo, isCurrentUserActive]);

    // Normalize filter type to 'any' if needed
    React.useEffect(() => {
        const currentItem = filter.filterInfo[0];
        if (!currentItem) return;

        const signature = JSON.stringify({
            type: currentItem.type,
            value: currentItem.value,
            valueLabel: currentItem.valueLabel,
            isCurrentUser: currentItem.isCurrentUser,
        });

        if (lastProcessedFilterRef.current === signature) return;
        if (isInternalUpdateRef.current) {
            isInternalUpdateRef.current = false;
            lastProcessedFilterRef.current = signature;
            return;
        }

        if (currentItem.type !== "any" && currentItem.type !== "current_user" && currentItem.type !== "selected_user") {
            const updatedFilterInfo: IFilterItem[] = [
                { ...currentItem, type: "any" },
            ];
            lastProcessedFilterRef.current = JSON.stringify({
                type: "any",
                value: currentItem.value,
                valueLabel: currentItem.valueLabel,
                isCurrentUser: currentItem.isCurrentUser,
            });
            const didNotify = notifyChange({
                ...filter,
                filterInfo: updatedFilterInfo,
            });
            if (didNotify) isInternalUpdateRef.current = true;
            return;
        }
        lastProcessedFilterRef.current = signature;
    }, [filter, filter.filterInfo, notifyChange]);

    // Sync from filter (single value)
    React.useEffect(() => {
        const currentItem = filter.filterInfo[0];
        if (!currentItem) {
            setSelectedKey(null);
            setInputValue("");
            return;
        }

        const signature = JSON.stringify({
            type: currentItem.type,
            value: currentItem.value,
            valueLabel: currentItem.valueLabel,
            isCurrentUser: currentItem.isCurrentUser,
        });

        if (lastProcessedFilterRef.current === signature && isInternalUpdateRef.current) {
            isInternalUpdateRef.current = false;
            return;
        }

        const valueStr = (currentItem.value || "").trim();
        const valueLabelStr = (currentItem.valueLabel || "").trim();

        if (currentItem.isCurrentUser === true && currentUserInfo) {
            setSelectedKey(String(currentUserInfo.id));
            setInputValue(currentUserInfo.name);
            if (!valueStr || valueStr !== String(currentUserInfo.id)) {
                const updatedFilterInfo: IFilterItem[] = [
                    {
                        ...currentItem,
                        type: "any",
                        value: String(currentUserInfo.id),
                        valueLabel: currentUserInfo.name,
                        isCurrentUser: true,
                    },
                ];
                lastProcessedFilterRef.current = JSON.stringify({
                    type: "any",
                    value: String(currentUserInfo.id),
                    valueLabel: currentUserInfo.name,
                    isCurrentUser: true,
                });
                const didNotify = notifyChange({
                    ...filter,
                    filterInfo: updatedFilterInfo,
                });
                if (didNotify) {
                    isInternalUpdateRef.current = true;
                    lastCommittedFilterRef.current = {
                        value: String(currentUserInfo.id),
                        valueLabel: currentUserInfo.name,
                        operator: "current_user",
                        isCurrentUser: true,
                    };
                }
                return;
            }
        } else {
            setSelectedKey(valueStr ? valueStr : null);
            setInputValue(valueLabelStr || "");
        }

        setOperator(currentItem.isCurrentUser === true ? "current_user" : "selected_user");
        lastCommittedFilterRef.current = {
            value: valueStr,
            valueLabel: valueLabelStr,
            operator: currentItem.isCurrentUser === true ? "current_user" : "selected_user",
            isCurrentUser: currentItem.isCurrentUser || false,
        };
        lastProcessedFilterRef.current = signature;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.filterInfo, currentUserInfo]);

    const list = useAsyncList<User>({
        async load({ cursor, filterText }) {
            if (!filterText || filterText.trim().length < 2) {
                return { items: [], cursor: undefined };
            }
            if (apiDebounceRef.current) clearTimeout(apiDebounceRef.current);

            return new Promise((resolve) => {
                apiDebounceRef.current = setTimeout(async () => {
                    try {
                        const offset = cursor ? Number(cursor) : 0;
                        const limit = 50;
                        const response = await accessService.listUser(
                            new ListUserRequest(
                                store.Server,
                                store.SessionId,
                                true,
                                limit,
                                offset,
                                undefined,
                                false,
                                undefined,
                                undefined,
                                undefined,
                                false,
                                undefined,
                                undefined,
                                filterText
                            )
                        );
                        const users = Array.isArray(response?.USERS)
                            ? response?.USERS?.map((u: any) => u.USER)
                            : [(response?.USERS as any)?.USER];
                        const mappedUsers: User[] = (users || []).map((user: any) => ({
                            id: user.id,
                            name:
                                user.contactName ||
                                user.name1 ||
                                user.userName ||
                                String(user.id),
                        }));

                        resolve({
                            items: mappedUsers,
                            cursor:
                                mappedUsers.length === limit
                                    ? String(offset + limit)
                                    : undefined,
                        });
                    } catch (error) {
                        console.error("Error fetching users:", error);
                        resolve({ items: [], cursor: undefined });
                    }
                }, 500);
            });
        },
    });

    const updateFilterInfo = React.useCallback(
        (newValue: string, newValueLabel: string, newOperator: string, newIsCurrentUser: boolean) => {
            if (!hasFilterValueChanged(newValue, newValueLabel, newOperator, newIsCurrentUser)) return;

            const updatedFilterInfo: IFilterItem[] = [
                {
                    ...currentFilterItem,
                    type: "any",
                    value: newValue,
                    valueLabel: newValueLabel,
                    isCurrentUser: newIsCurrentUser,
                },
            ];

            lastProcessedFilterRef.current = JSON.stringify({
                type: "any",
                value: newValue,
                valueLabel: newValueLabel,
                isCurrentUser: newIsCurrentUser,
            });

            const didNotify = notifyChange({
                ...filter,
                filterInfo: updatedFilterInfo,
            });

            if (didNotify) {
                isInternalUpdateRef.current = true;
                lastCommittedFilterRef.current = {
                    value: newValue,
                    valueLabel: newValueLabel,
                    operator: newOperator,
                    isCurrentUser: newIsCurrentUser,
                };
            }
        },
        [filter, currentFilterItem, notifyChange, hasFilterValueChanged]
    );

    const handleSelectionChange = React.useCallback(
        (key: Key | null) => {
            if (isDisabled) return;
            if (selectedKey === (key ? String(key) : null)) return;

            setSelectedKey(key);

            if (key === null) {
                setInputValue("");
                updateFilterInfo("", "", "selected_user", false);
                return;
            }

            const selectedUser = list.items.find((u) => String(u.id) === String(key));
            if (selectedUser) {
                setInputValue(selectedUser.name);
                updateFilterInfo(
                    String(selectedUser.id),
                    selectedUser.name,
                    "selected_user",
                    false
                );
            } else {
                const fallbackLabel = currentFilterItem.valueLabel || "";
                updateFilterInfo(String(key), fallbackLabel, "selected_user", false);
            }
        },
        [isDisabled, selectedKey, list.items, currentFilterItem.valueLabel, updateFilterInfo]
    );

    const handleInputChange = React.useCallback(
        (value: string) => {
            if (isDisabled) return;
            setInputValue(value);
            if (!value) {
                setSelectedKey(null);
                updateFilterInfo("", "", "selected_user", false);
            }
            list.setFilterText(value);
        },
        [isDisabled, list, updateFilterInfo]
    );

    const handleOperatorChange = React.useCallback(
        (newOperator: string) => {
            if (operator === newOperator) return;

            setOperator(newOperator);

            if (newOperator === "current_user" && currentUserInfo) {
                setSelectedKey(String(currentUserInfo.id));
                setInputValue(currentUserInfo.name);
                updateFilterInfo(
                    String(currentUserInfo.id),
                    currentUserInfo.name,
                    "current_user",
                    true
                );
            } else {
                const value = currentFilterItem.value || "";
                const valueLabel = currentFilterItem.valueLabel || "";
                updateFilterInfo(value, valueLabel, "selected_user", false);
            }
        },
        [operator, currentUserInfo, currentFilterItem.value, currentFilterItem.valueLabel, updateFilterInfo]
    );

    React.useEffect(() => {
        return () => {
            if (apiDebounceRef.current) clearTimeout(apiDebounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div ref={forwardedRef} className={styles.responsibleUserFilterControl}>
            <Flex direction="column" gap="size-200">
                <div className={styles.header}>
                    <Text UNSAFE_className={styles.title}>
                        {filter.label || filter.name}
                    </Text>
                    <div className={styles.operator}>
                        <OperatorSelector
                            operators={userOperators}
                            selectedOperator={operator}
                            onOperatorChange={handleOperatorChange}
                            aria-label="Responsible user filter operator"
                        />
                    </div>
                </div>

                <View width="100%">
                    <ComboBox
                        aria-label="User search"
                        items={list.items}
                        selectedKey={selectedKey}
                        inputValue={inputValue}
                        onInputChange={handleInputChange}
                        onSelectionChange={handleSelectionChange}
                        loadingState={list.loadingState}
                        onLoadMore={list.loadMore}
                        allowsCustomValue={false}
                        isDisabled={isDisabled}
                        width="100%"
                        description={t("search_users_description", {
                            ns: "layout_components",
                            defaultValue: "Type to search users...",
                        })}
                    >
                        {(item) => (
                            <Item key={String(item.id)} textValue={item.name}>
                                {item.name}
                            </Item>
                        )}
                    </ComboBox>
                </View>
            </Flex>
        </div>
    );
});

ResponsibleUserFilterControl.displayName = "ResponsibleUserFilterControl";
